// Lightweight SVG charts (ported from the design project)
import { ReactNode } from "react";

function path(data: number[], w: number, h: number, pad = 0): [number, number][] {
  const max = Math.max(...data), min = Math.min(...data);
  const span = max - min || 1;
  const iw = w - pad * 2, ih = h - pad * 2;
  return data.map((v, i) => [pad + (i / (data.length - 1)) * iw, pad + ih - ((v - min) / span) * ih]);
}

export const Sparkline = ({ data, w = "100%", h = 32, color = "var(--brand)", fill = true }: {
  data: number[]; w?: number | string; h?: number; color?: string; fill?: boolean;
}) => {
  const W = 120, H = h;
  const pts = path(data, W, H, 2);
  const d = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const area = `${d} L${W - 2} ${H} L2 ${H} Z`;
  const gid = "sg" + Math.round(data[0] * data.length) + "-" + data.length;
  return (
    <svg width={w} height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
      {fill && <>
        <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={color} stopOpacity=".18" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>
        <path d={area} fill={`url(#${gid})`} />
      </>}
      <path d={d} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

// Multi-series line/area chart with axis + optional forecast dash
export const LineChart = ({ series, labels, height = 240, yFmt = (v: number) => String(v), forecastFrom, area = true }: {
  series: { data: number[]; color: string }[];
  labels?: string[];
  height?: number;
  yFmt?: (v: number) => string | number;
  forecastFrom?: number;
  area?: boolean;
}) => {
  const W = 760, H = height, padL = 48, padR = 14, padT = 14, padB = 26;
  const iw = W - padL - padR, ih = H - padT - padB;
  const all = series.flatMap((s) => s.data);
  const max = Math.max(...all) * 1.08, min = Math.min(0, ...all);
  const span = max - min || 1;
  const n = series[0].data.length;
  const X = (i: number) => padL + (i / (n - 1)) * iw;
  const Y = (v: number) => padT + ih - ((v - min) / span) * ih;
  const ticks = 4;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
      {Array.from({ length: ticks + 1 }).map((_, i) => {
        const v = min + (span / ticks) * i;
        const y = Y(v);
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--line-2)" />
            <text x={padL - 8} y={y + 3.5} textAnchor="end" fontSize="10.5" fill="var(--ink-4)" fontFamily="Geist Mono">{yFmt(v)}</text>
          </g>
        );
      })}
      {labels && labels.map((l, i) => (i % Math.ceil(n / 8) === 0 || i === n - 1) && (
        <text key={i} x={X(i)} y={H - 8} textAnchor="middle" fontSize="10.5" fill="var(--ink-4)">{l}</text>
      ))}
      {series.map((s, si) => {
        const pts: [number, number][] = s.data.map((v, i) => [X(i), Y(v)]);
        const solid = forecastFrom != null ? pts.slice(0, forecastFrom + 1) : pts;
        const dash = forecastFrom != null ? pts.slice(forecastFrom) : null;
        const dl = (a: [number, number][]) => a.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
        const gid = "lc" + si;
        return (
          <g key={si}>
            {area && si === 0 && <>
              <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={s.color} stopOpacity=".16" /><stop offset="1" stopColor={s.color} stopOpacity="0" /></linearGradient></defs>
              <path d={`${dl(pts)} L${X(n - 1)} ${Y(min)} L${X(0)} ${Y(min)} Z`} fill={`url(#${gid})`} />
            </>}
            <path d={dl(solid)} fill="none" stroke={s.color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            {dash && <path d={dl(dash)} fill="none" stroke={s.color} strokeWidth="2.2" strokeDasharray="2 5" strokeLinecap="round" />}
            {forecastFrom != null && <circle cx={X(forecastFrom)} cy={Y(s.data[forecastFrom])} r="3.2" fill="#fff" stroke={s.color} strokeWidth="2" />}
          </g>
        );
      })}
    </svg>
  );
};

// Grouped/stacked bar chart
export const BarChart = ({ data, height = 240, colors = ["var(--brand)", "var(--ink-5)"], yFmt = (v: number) => String(v), keys = ["a"], labels }: {
  data: Record<string, number | string>[];
  height?: number;
  colors?: string[];
  yFmt?: (v: number) => string | number;
  keys?: string[];
  labels?: string[];
}) => {
  const W = 760, H = height, padL = 48, padR = 14, padT = 14, padB = 26;
  const iw = W - padL - padR, ih = H - padT - padB;
  const max = Math.max(...data.map((d) => keys.reduce((s, k) => s + ((d[k] as number) || 0), 0))) * 1.1 || 1;
  const n = data.length;
  const bw = Math.min(38, (iw / n) * 0.6);
  const step = iw / n;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
        const y = padT + ih - f * ih;
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--line-2)" />
            <text x={padL - 8} y={y + 3.5} textAnchor="end" fontSize="10.5" fill="var(--ink-4)" fontFamily="Geist Mono">{yFmt(max * f)}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const cx = padL + step * i + step / 2;
        let acc = 0;
        return (
          <g key={i}>
            {keys.map((k, ki) => {
              const v = (d[k] as number) || 0;
              const h = (v / max) * ih;
              const y = padT + ih - acc - h;
              acc += h;
              return <rect key={k} x={cx - bw / 2} y={y} width={bw} height={Math.max(0, h)} rx={ki === keys.length - 1 ? 4 : 0} fill={colors[ki]} />;
            })}
            <text x={cx} y={H - 8} textAnchor="middle" fontSize="10.5" fill="var(--ink-4)">{labels ? labels[i] : (d.label as string)}</text>
          </g>
        );
      })}
    </svg>
  );
};

// Donut
export const Donut = ({ data, size = 150, thickness = 22, center }: {
  data: { value: number; color: string }[];
  size?: number;
  thickness?: number;
  center?: ReactNode;
}) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  const r = size / 2 - thickness / 2;
  const c = 2 * Math.PI * r;
  let off = 0;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth={thickness} />
        {data.map((d, i) => {
          const len = (d.value / total) * c;
          const el = <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={d.color} strokeWidth={thickness} strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-off} strokeLinecap="butt" />;
          off += len;
          return el;
        })}
      </svg>
      {center && <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>{center}</div>}
    </div>
  );
};

/* Flow chart: grouped inflow/outflow bars + net line + forecast split */
export const FlowChart = ({ data, height = 300, forecastFrom, inColor = "#2f6bff", outColor = "#DC2626", netColor = "#16A34A", yFmt = (v: number) => String(v) }: {
  data: { label: string; in: number; out: number }[];
  height?: number;
  forecastFrom?: number;
  inColor?: string;
  outColor?: string;
  netColor?: string;
  yFmt?: (v: number) => string | number;
}) => {
  const W = 980, H = height, padL = 52, padR = 16, padT = 18, padB = 30;
  const iw = W - padL - padR, ih = H - padT - padB;
  const maxV = Math.max(...data.flatMap((d) => [d.in, d.out])) * 1.12 || 1;
  const n = data.length;
  const step = iw / n;
  const gap = 4;
  const bw = Math.min(15, (step - gap * 3) / 2);
  const Y = (v: number) => padT + ih - (v / maxV) * ih;
  const nY = (v: number) => padT + ih - (v / maxV) * ih;
  const netPts: [number, number][] = data.map((d, i) => [padL + step * i + step / 2, nY(d.in - d.out)]);
  const dl = (a: [number, number][]) => a.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const solidNet = forecastFrom != null ? netPts.slice(0, forecastFrom + 1) : netPts;
  const dashNet = forecastFrom != null ? netPts.slice(forecastFrom) : null;
  const fx = forecastFrom != null ? padL + step * forecastFrom : null;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
      {fx != null && <rect x={fx} y={padT} width={W - padR - fx} height={ih} fill="var(--surface-3)" opacity=".45" />}
      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
        const y = padT + ih - f * ih;
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--line-2)" />
            <text x={padL - 9} y={y + 3.5} textAnchor="end" fontSize="10.5" fill="var(--ink-4)" fontFamily="Geist Mono">{yFmt(maxV * f)}</text>
          </g>
        );
      })}
      {fx != null && <g>
        <line x1={fx} y1={padT - 4} x2={fx} y2={padT + ih} stroke="var(--ink-4)" strokeWidth="1" strokeDasharray="3 3" />
        <text x={fx + 7} y={padT + 9} fontSize="11" fontWeight="700" fill="var(--ink-2)">Today</text>
        <text x={fx + 7} y={padT + 23} fontSize="10.5" fill="var(--ink-4)">Forecast →</text>
      </g>}
      {data.map((d, i) => {
        const cx = padL + step * i + step / 2;
        const fc = forecastFrom != null && i >= forecastFrom;
        return (
          <g key={i}>
            <rect x={cx - bw - 1} y={Y(d.in)} width={bw} height={padT + ih - Y(d.in)} rx={3} fill={inColor} opacity={fc ? 0.42 : 1} />
            <rect x={cx + 1} y={Y(d.out)} width={bw} height={padT + ih - Y(d.out)} rx={3} fill={outColor} opacity={fc ? 0.42 : 1} />
            {(i % 2 === 0 || i === n - 1) && <text x={cx} y={H - 9} textAnchor="middle" fontSize="10.5" fill="var(--ink-4)">{d.label}</text>}
          </g>
        );
      })}
      <path d={dl(solidNet)} fill="none" stroke={netColor} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      {dashNet && <path d={dl(dashNet)} fill="none" stroke={netColor} strokeWidth="2.4" strokeDasharray="3 5" strokeLinecap="round" />}
    </svg>
  );
};
